import orderBy from 'lodash/orderBy';
import { CRMContact, CRMToday } from '../../../types';
import { contactTags } from './contact-utils';

const orderByMonthAndDay = (params: {
  contacts: CRMContact[];
}): CRMContact[] => {
  // Create an array that can be ordered by birth month and day
  const birthData: {
    contact: CRMContact;
    birthMonth: number;
    birthDay: number;
  }[] = [];
  params.contacts.forEach((contact) => {
    if (contact.DateOfBirth) {
      const birthDate = new Date(contact.DateOfBirth);
      birthData.push({
        contact,
        birthMonth: birthDate.getMonth(),
        birthDay: birthDate.getDate(),
      });
    }
  });

  // Order the array by birth month an day
  const orderedBirthData = orderBy(birthData, ['birthMonth', 'birthDay']);

  // Return the array of contacts passed into the function
  // Now ordered by birth month and day
  const contactsToReturn: CRMContact[] = [];
  orderedBirthData.forEach((data) => {
    contactsToReturn.push(data.contact);
  });

  return contactsToReturn;
};

export const getData = (params: { data: CRMContact[] }) => {
  return {
    // POLICY REVIEWS
    reviewMonth6: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.reviewMonth6)
    ),
    reviewMonth12: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.reviewMonth12)
    ),
    // CONVERSIONS
    conversionFOR: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.conversionFOR)
    ),
    conversionMOO: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.conversionMOO)
    ),
    conversionCFG: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.conversionCFG)
    ),
    // CHARGEBACKS
    chargebacks: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.chargeback)
    ),
    // BIRTHDAYS
    birthdayToday: params.data.filter((contact) =>
      contact.contactTags?.includes(contactTags.birthdayToday)
    ),
    // Order Recent and Upcoming Birthdays by their day and month
    // Not taking into account the year the person was born
    birthdayRecent: orderByMonthAndDay({
      contacts: params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.birthdayRecent)
      ),
    }),
    birthdayUpcoming: orderByMonthAndDay({
      contacts: params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.birthdayUpcoming)
      ),
    }),

    // MEDICARE
    medicareToday: orderBy(
      params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.medicareToday)
      ),
      'DateOfBirth'
    ),
    medicareIn1: orderBy(
      params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.medicareIn1)
      ),
      'DateOfBirth'
    ),
    medicareIn2: orderBy(
      params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.medicareIn2)
      ),
      'DateOfBirth'
    ),
    medicareIn3: orderBy(
      params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.medicareIn3)
      ),
      'DateOfBirth'
    ),
    medicareIn6: orderBy(
      params.data.filter((contact) =>
        contact.contactTags?.includes(contactTags.medicareIn6)
      ),
      'DateOfBirth'
    ),

    // Mutual of Omaha Children's WL
    mooChildrensWL: params.data.filter((contact) => contact.MoOChildrensWL),

    // Foresters Children's WL
    forestersChildrensWL: params.data.filter(
      (contact) => contact.ForestersChildrensWL
    ),
  };
};

export interface TodayTotals {
  todayCount: number;
  reviewCount: number;
  conversionCount: number;
  chargebackCount: number;
  birthdayCount: number;
  medicareCount: number;
  forestersCount: number;
  mooCount: number;
}

export const getTodayTotals = (params: {
  data: CRMToday;
  showCompleted: boolean;
}): TodayTotals => {
  const totals: TodayTotals = {
    todayCount: 0,
    reviewCount: 0,
    conversionCount: 0,
    chargebackCount: 0,
    birthdayCount: 0,
    medicareCount: 0,
    forestersCount: 0,
    mooCount: 0,
  };

  // Policy Reviews
  if (params.showCompleted) {
    // All
    totals.reviewCount = params.data.policyReviews.length;
  } else {
    // Uncompleted
    totals.reviewCount = params.data.policyReviews.filter(
      (i) => !i.Completed
    ).length;
  }

  // Conversions
  if (params.showCompleted) {
    // All
    totals.conversionCount = params.data.conversions.length;
  } else {
    // Uncompleted
    totals.conversionCount = params.data.conversions.filter(
      (i) => !i.Completed
    ).length;
  }

  // Chargebacks
  if (params.showCompleted) {
    // All
    totals.chargebackCount = params.data.chargebacks.length;
  } else {
    // Uncompleted
    totals.chargebackCount = params.data.chargebacks.filter(
      (i) => !i.Completed
    ).length;
  }

  // Medicare
  if (params.showCompleted) {
    // All
    totals.medicareCount = params.data.medicare.length;
  } else {
    // Uncompleted
    totals.medicareCount = params.data.medicare.filter(
      (i) => !i.Completed
    ).length;
  }

  // Birthday
  if (params.showCompleted) {
    // All
    totals.birthdayCount = params.data.birthdays.length;
  } else {
    // Uncompleted
    totals.birthdayCount = params.data.birthdays.filter(
      (i) => !i.Completed
    ).length;
  }

  // Childrens WL - Mutual of Omaha
  if (params.showCompleted) {
    // All
    totals.mooCount = params.data.moo_children_wl.length;
  } else {
    totals.mooCount = params.data.moo_children_wl?.filter(
      (i) => !i.Completed
    ).length;
  }

  // Children's WL - Foresters
  if (params.showCompleted) {
    // All
    totals.forestersCount = params.data.foresters_children_wl.length;
  } else {
    totals.forestersCount = params.data.foresters_children_wl?.filter(
      (i) => !i.Completed
    ).length;
  }

  // The todayCount will be shown on the button that
  // is used to select the Today tab of the CRM
  totals.todayCount =
    totals.birthdayCount +
    totals.conversionCount +
    totals.chargebackCount +
    totals.medicareCount +
    totals.reviewCount +
    totals.mooCount +
    totals.forestersCount;

  return totals;
};

export const getSubTotals = (params: {
  data: {
    reviewMonth6: CRMContact[];
    reviewMonth12: CRMContact[];
    conversionFOR: CRMContact[];
    conversionMOO: CRMContact[];
    conversionCFG: CRMContact[];
    birthdayToday: CRMContact[];
    birthdayRecent: CRMContact[];
    birthdayUpcoming: CRMContact[];
    medicareToday: CRMContact[];
    medicareIn1: CRMContact[];
    medicareIn2: CRMContact[];
    medicareIn3: CRMContact[];
    medicareIn6: CRMContact[];
  };
  showCompleted: boolean;
}) => {
  // Params
  const { data, showCompleted } = params;

  const subTotals = {
    reviewMonth6: 0,
    reviewMonth12: 0,
    conversionFOR: 0,
    conversionMOO: 0,
    conversionCFG: 0,
    birthdayToday: 0,
    birthdayRecent: 0,
    birthdayUpcoming: 0,
    medicareToday: 0,
    medicareIn1: 0,
    medicareIn2: 0,
    medicareIn3: 0,
    medicareIn6: 0,
  };
  if (showCompleted) {
    // Subtotal All
    // reviewMonth6
    data.reviewMonth6.forEach((item) => {
      item.Reviews?.forEach((i) => {
        if (i.PolicyReview === '6mo') {
          subTotals.reviewMonth6++;
        }
      });
    });
    // reviewMonth12
    data.reviewMonth12.forEach((item) => {
      item.Reviews?.forEach((i) => {
        if (i.PolicyReview === '12mo') {
          subTotals.reviewMonth12++;
        }
      });
    });
    // conversionFOR
    data.conversionFOR.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (i.PolicyReview === 'Foresters Strong Foundation') {
          subTotals.conversionFOR++;
        }
      });
    });
    // conversionMOO
    data.conversionMOO.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (i.PolicyReview === 'Mutual of Omaha Term Life Express') {
          subTotals.conversionMOO++;
        }
      });
    });
    // conversionCFG
    data.conversionCFG.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (i.PolicyReview === 'CFG SafeShield') {
          subTotals.conversionCFG++;
        }
      });
    });
    // birthdayToday
    data.birthdayToday.forEach((i) => {
      subTotals.birthdayToday++;
    });
    // birthdayRecent
    data.birthdayRecent.forEach((i) => {
      subTotals.birthdayRecent++;
    });
    // birthdayUpcoming
    data.birthdayUpcoming.forEach((i) => {
      subTotals.birthdayUpcoming++;
    });
    // medicareToday
    data.medicareToday.forEach((i) => {
      subTotals.medicareToday++;
    });
    // medicareIn1
    data.medicareIn1.forEach((i) => {
      subTotals.medicareIn1++;
    });
    // medicareIn2
    data.medicareIn2.forEach((i) => {
      subTotals.medicareIn2++;
    });
    // medicareIn3
    data.medicareIn3.forEach((i) => {
      subTotals.medicareIn3++;
    });
    // medicareIn6
    data.medicareIn6.forEach((i) => {
      subTotals.medicareIn6++;
    });
  } else {
    // Subtotal Uncompleted
    // reviewMonth6
    data.reviewMonth6.forEach((item) => {
      item.Reviews?.forEach((i) => {
        if (i.PolicyReview === '6mo' && !i.Completed) {
          subTotals.reviewMonth6++;
        }
      });
    });
    // reviewMonth12
    data.reviewMonth12.forEach((item) => {
      item.Reviews?.forEach((i) => {
        if (i.PolicyReview === '12mo' && !i.Completed) {
          subTotals.reviewMonth12++;
        }
      });
    });
    // conversionFOR
    data.conversionFOR.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (i.PolicyReview === 'Foresters Strong Foundation' && !i.Completed) {
          subTotals.conversionFOR++;
        }
      });
    });
    // conversionMOO
    data.conversionMOO.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (
          i.PolicyReview === 'Mutual of Omaha Term Life Express' &&
          !i.Completed
        ) {
          subTotals.conversionMOO++;
        }
      });
    });
    // conversionCFG
    data.conversionCFG.forEach((item) => {
      item.Conversions?.forEach((i) => {
        if (i.PolicyReview === 'CFG SafeShield' && !i.Completed) {
          subTotals.conversionCFG++;
        }
      });
    });
    // birthdayToday
    data.birthdayToday.forEach((i) => {
      if (!i.Birthday?.Completed) {
        subTotals.birthdayToday++;
      }
    });
    // birthdayRecent
    data.birthdayRecent.forEach((i) => {
      if (!i.Birthday?.Completed) {
        subTotals.birthdayRecent++;
      }
    });
    // birthdayUpcoming
    data.birthdayUpcoming.forEach((i) => {
      if (!i.Birthday?.Completed) {
        subTotals.birthdayUpcoming++;
      }
    });
    // medicareToday
    data.medicareToday.forEach((i) => {
      if (!i.Medicare?.Completed) {
        subTotals.medicareToday++;
      }
    });
    // medicareIn1
    data.medicareIn1.forEach((i) => {
      if (!i.Medicare?.Completed) {
        subTotals.medicareIn1++;
      }
    });
    // medicareIn2
    data.medicareIn2.forEach((i) => {
      if (!i.Medicare?.Completed) {
        subTotals.medicareIn2++;
      }
    });
    // medicareIn3
    data.medicareIn3.forEach((i) => {
      if (!i.Medicare?.Completed) {
        subTotals.medicareIn3++;
      }
    });
    // medicareIn6
    data.medicareIn6.forEach((i) => {
      if (!i.Medicare?.Completed) {
        subTotals.medicareIn6++;
      }
    });
  }

  return subTotals;
};
